import type { LangCode } from "../data/languages";

type Key =
  // nav / actions
  | "back" | "continue" | "cancel" | "yes" | "no"
  | "need_help" | "come_to_bar_title" | "come_to_bar_sub"
  // card-only banner
  | "card_only_main" | "card_only_sub"
  // language picker (kept centered/multilingual on its own page, not localized)
  // menu
  | "menu_word" | "menu_table_of" | "menu_pick" | "menu_things"
  | "menu_view_order" | "menu_restart" | "menu_pay"
  | "menu_add" | "menu_update" | "menu_remove" | "menu_edit_label" | "menu_add_label"
  | "restart_title" | "restart_body" | "restart_yes"
  // receipt
  | "receipt_your_account" | "receipt_total" | "receipt_items_one" | "receipt_items_many"
  // name
  | "name_form_label" | "name_required" | "name_caption" | "name_title" | "name_handwritten" | "name_tag"
  // upsell
  | "upsell_one_more" | "upsell_tap_open" | "upsell_from_chef" | "upsell_no_thanks" | "upsell_yes_add" | "upsell_ps"
  // review
  | "review_caption" | "review_title" | "review_handwritten" | "review_draft"
  | "review_customer" | "review_per_unit" | "review_total" | "review_tip_note"
  | "review_edit" | "review_confirm"
  // terminal guide
  | "guide_in_progress" | "guide_step_word" | "guide_order"
  | "guide_help" | "guide_cancel" | "guide_help_back"
  | "step_1" | "step_2" | "step_3" | "step_4"
  // failure
  | "fail_caption" | "fail_canceled" | "fail_expired" | "fail_rejected" | "fail_network"
  | "fail_no_charge" | "fail_edit" | "fail_try_again"
  // thank you
  | "thanks_caption" | "thanks_handwritten" | "thanks_show_title" | "thanks_show_sub" | "thanks_order";

type Bundle = Record<Key, string>;

const en: Bundle = {
  back: "Back", continue: "Continue", cancel: "Cancel", yes: "Yes", no: "No",
  need_help: "Need help?",
  come_to_bar_title: "Come to the bar",
  come_to_bar_sub: "We'll help you out.",
  card_only_main: "CARD ONLY",
  card_only_sub: "WE DO NOT ACCEPT CASH",
  menu_word: "Menu", menu_table_of: "table of", menu_pick: "pick your card", menu_things: "items",
  menu_view_order: "View order", menu_restart: "Restart", menu_pay: "Pay",
  menu_add: "Add", menu_update: "Update", menu_remove: "Remove from order",
  menu_edit_label: "EDIT", menu_add_label: "ADD",
  restart_title: "Restart?", restart_body: "This will clear your order and go back to the start.", restart_yes: "Yes, restart",
  receipt_your_account: "Your tab",
  receipt_total: "TOTAL", receipt_items_one: "item", receipt_items_many: "items",
  name_form_label: "FORM · 02", name_required: "REQUIRED",
  name_caption: "So we can call you",
  name_title: "What's your name?",
  name_handwritten: "we'll call it out when ready",
  name_tag: "Hello, my name is",
  upsell_one_more: "one more thing…", upsell_tap_open: "tap to open",
  upsell_from_chef: "FROM THE CHEF", upsell_no_thanks: "No, thanks", upsell_yes_add: "Yes, add",
  upsell_ps: "P.S.",
  review_caption: "Confirm your order", review_title: "Review before paying",
  review_handwritten: "double-check", review_draft: "RECEIPT · DRAFT",
  review_customer: "CUSTOMER", review_per_unit: "ea", review_total: "TOTAL",
  review_tip_note: "* tip is selected on the terminal",
  review_edit: "Edit order", review_confirm: "Confirm & pay",
  guide_in_progress: "payment in progress", guide_step_word: "Step", guide_order: "ORDER",
  guide_help: "HELP", guide_cancel: "CANCEL", guide_help_back: "Back",
  step_1: "Walk to the terminal",
  step_2: "Pick a tip on the screen",
  step_3: "Tap or insert your card",
  step_4: "Press the green button",
  fail_caption: "almost…",
  fail_canceled: "You canceled the payment", fail_expired: "Time ran out",
  fail_rejected: "Your card was declined", fail_network: "We couldn't connect",
  fail_no_charge: "No charge was made.", fail_edit: "Edit order", fail_try_again: "Try again",
  thanks_caption: "Thanks", thanks_handwritten: "cheers,",
  thanks_show_title: "Bring this receipt to the bar",
  thanks_show_sub: "Your drinks are coming up.",
  thanks_order: "ORDER",
};

const es: Bundle = {
  back: "Atrás", continue: "Continuar", cancel: "Cancelar", yes: "Sí", no: "No",
  need_help: "¿Necesitas ayuda?",
  come_to_bar_title: "Acércate a la barra",
  come_to_bar_sub: "Te ayudamos en un momento.",
  card_only_main: "SOLO TARJETA",
  card_only_sub: "NO ACEPTAMOS EFECTIVO",
  menu_word: "Menú", menu_table_of: "tabla de", menu_pick: "elige tu carta", menu_things: "cosas",
  menu_view_order: "Ver orden", menu_restart: "Reiniciar", menu_pay: "Pagar",
  menu_add: "Agregar", menu_update: "Actualizar", menu_remove: "Eliminar de la orden",
  menu_edit_label: "EDITAR", menu_add_label: "AGREGAR",
  restart_title: "¿Reiniciar?", restart_body: "Esto borrará tu orden y volverá al inicio.", restart_yes: "Sí, reiniciar",
  receipt_your_account: "Tu cuenta",
  receipt_total: "TOTAL", receipt_items_one: "ítem", receipt_items_many: "ítems",
  name_form_label: "FORMA · 02", name_required: "REQUERIDO",
  name_caption: "Para llamar tu orden",
  name_title: "¿Cómo te llamas?",
  name_handwritten: "te llamaremos cuando esté lista",
  name_tag: "Hola, me llamo",
  upsell_one_more: "una última cosita…", upsell_tap_open: "tap para abrir",
  upsell_from_chef: "DEL CHEF", upsell_no_thanks: "No, gracias", upsell_yes_add: "Sí, agrega",
  upsell_ps: "P.D.",
  review_caption: "Confirmar tu orden", review_title: "Revisa antes de pagar",
  review_handwritten: "revisa con calma", review_draft: "RECIBO · BORRADOR",
  review_customer: "CLIENTE", review_per_unit: "c/u", review_total: "TOTAL",
  review_tip_note: "* la propina se elige en la terminal",
  review_edit: "Editar orden", review_confirm: "Confirmar y pagar",
  guide_in_progress: "pago en curso", guide_step_word: "Paso", guide_order: "ORDEN",
  guide_help: "AYUDA", guide_cancel: "CANCELAR", guide_help_back: "Volver",
  step_1: "Acércate a la terminal",
  step_2: "Elige tu propina",
  step_3: "Acerca o inserta tu tarjeta",
  step_4: "Presiona el botón verde",
  fail_caption: "casi…",
  fail_canceled: "Cancelaste el pago", fail_expired: "Se acabó el tiempo",
  fail_rejected: "Tu tarjeta fue rechazada", fail_network: "No pudimos conectarnos",
  fail_no_charge: "No se hizo ningún cargo.", fail_edit: "Cambiar orden", fail_try_again: "Intentar de nuevo",
  thanks_caption: "Gracias", thanks_handwritten: "¡salud,",
  thanks_show_title: "Lleva tu recibo a la barra",
  thanks_show_sub: "Tus bebidas vienen en camino.",
  thanks_order: "ORDEN",
};

const pt: Bundle = {
  back: "Voltar", continue: "Continuar", cancel: "Cancelar", yes: "Sim", no: "Não",
  need_help: "Precisa de ajuda?",
  come_to_bar_title: "Vá até o balcão",
  come_to_bar_sub: "A gente te ajuda já.",
  card_only_main: "SÓ CARTÃO",
  card_only_sub: "NÃO ACEITAMOS DINHEIRO",
  menu_word: "Cardápio", menu_table_of: "mesa de", menu_pick: "escolha sua carta", menu_things: "itens",
  menu_view_order: "Ver pedido", menu_restart: "Recomeçar", menu_pay: "Pagar",
  menu_add: "Adicionar", menu_update: "Atualizar", menu_remove: "Remover do pedido",
  menu_edit_label: "EDITAR", menu_add_label: "ADICIONAR",
  restart_title: "Recomeçar?", restart_body: "Isso vai apagar seu pedido e voltar ao início.", restart_yes: "Sim, recomeçar",
  receipt_your_account: "Sua conta",
  receipt_total: "TOTAL", receipt_items_one: "item", receipt_items_many: "itens",
  name_form_label: "FORM · 02", name_required: "OBRIGATÓRIO",
  name_caption: "Para chamarmos seu pedido",
  name_title: "Qual seu nome?",
  name_handwritten: "te chamamos quando estiver pronto",
  name_tag: "Olá, me chamo",
  upsell_one_more: "uma última coisa…", upsell_tap_open: "toque para abrir",
  upsell_from_chef: "DO CHEF", upsell_no_thanks: "Não, obrigado", upsell_yes_add: "Sim, adicione",
  upsell_ps: "P.S.",
  review_caption: "Confirme seu pedido", review_title: "Revise antes de pagar",
  review_handwritten: "confira com calma", review_draft: "RECIBO · RASCUNHO",
  review_customer: "CLIENTE", review_per_unit: "c/u", review_total: "TOTAL",
  review_tip_note: "* a gorjeta é escolhida no terminal",
  review_edit: "Editar pedido", review_confirm: "Confirmar e pagar",
  guide_in_progress: "pagamento em andamento", guide_step_word: "Passo", guide_order: "PEDIDO",
  guide_help: "AJUDA", guide_cancel: "CANCELAR", guide_help_back: "Voltar",
  step_1: "Vá até o terminal",
  step_2: "Escolha sua gorjeta",
  step_3: "Aproxime ou insira o cartão",
  step_4: "Aperte o botão verde",
  fail_caption: "quase…",
  fail_canceled: "Você cancelou o pagamento", fail_expired: "O tempo acabou",
  fail_rejected: "Seu cartão foi recusado", fail_network: "Não conseguimos conectar",
  fail_no_charge: "Nenhum valor foi cobrado.", fail_edit: "Editar pedido", fail_try_again: "Tentar de novo",
  thanks_caption: "Obrigado", thanks_handwritten: "saúde,",
  thanks_show_title: "Leve este recibo até o balcão",
  thanks_show_sub: "Suas bebidas estão a caminho.",
  thanks_order: "PEDIDO",
};

const fr: Bundle = {
  back: "Retour", continue: "Continuer", cancel: "Annuler", yes: "Oui", no: "Non",
  need_help: "Besoin d'aide ?",
  come_to_bar_title: "Approchez-vous du bar",
  come_to_bar_sub: "On vous aide tout de suite.",
  card_only_main: "CARTE UNIQUEMENT",
  card_only_sub: "NOUS N'ACCEPTONS PAS D'ESPÈCES",
  menu_word: "Menu", menu_table_of: "table de", menu_pick: "choisis ta carte", menu_things: "articles",
  menu_view_order: "Voir commande", menu_restart: "Recommencer", menu_pay: "Payer",
  menu_add: "Ajouter", menu_update: "Mettre à jour", menu_remove: "Retirer de la commande",
  menu_edit_label: "MODIFIER", menu_add_label: "AJOUTER",
  restart_title: "Recommencer ?", restart_body: "Cela effacera ta commande et reviendra au début.", restart_yes: "Oui, recommencer",
  receipt_your_account: "Ton addition",
  receipt_total: "TOTAL", receipt_items_one: "article", receipt_items_many: "articles",
  name_form_label: "FORM · 02", name_required: "OBLIGATOIRE",
  name_caption: "Pour appeler ta commande",
  name_title: "Comment t'appelles-tu ?",
  name_handwritten: "on t'appellera quand c'est prêt",
  name_tag: "Bonjour, je m'appelle",
  upsell_one_more: "une dernière chose…", upsell_tap_open: "appuie pour ouvrir",
  upsell_from_chef: "DU CHEF", upsell_no_thanks: "Non, merci", upsell_yes_add: "Oui, ajoute",
  upsell_ps: "P.S.",
  review_caption: "Confirme ta commande", review_title: "Vérifie avant de payer",
  review_handwritten: "vérifie tranquillement", review_draft: "REÇU · BROUILLON",
  review_customer: "CLIENT", review_per_unit: "u", review_total: "TOTAL",
  review_tip_note: "* le pourboire se choisit sur le terminal",
  review_edit: "Modifier la commande", review_confirm: "Confirmer et payer",
  guide_in_progress: "paiement en cours", guide_step_word: "Étape", guide_order: "COMMANDE",
  guide_help: "AIDE", guide_cancel: "ANNULER", guide_help_back: "Retour",
  step_1: "Va vers le terminal",
  step_2: "Choisis ton pourboire",
  step_3: "Approche ou insère ta carte",
  step_4: "Appuie sur le bouton vert",
  fail_caption: "presque…",
  fail_canceled: "Tu as annulé le paiement", fail_expired: "Le temps est écoulé",
  fail_rejected: "Ta carte a été refusée", fail_network: "Connexion impossible",
  fail_no_charge: "Aucun montant n'a été débité.", fail_edit: "Modifier la commande", fail_try_again: "Réessayer",
  thanks_caption: "Merci", thanks_handwritten: "santé,",
  thanks_show_title: "Apporte ce reçu au bar",
  thanks_show_sub: "Tes boissons arrivent.",
  thanks_order: "CMD",
};

const de: Bundle = {
  back: "Zurück", continue: "Weiter", cancel: "Abbrechen", yes: "Ja", no: "Nein",
  need_help: "Brauchst du Hilfe?",
  come_to_bar_title: "Komm zur Bar",
  come_to_bar_sub: "Wir helfen dir gleich.",
  card_only_main: "NUR KARTE",
  card_only_sub: "WIR AKZEPTIEREN KEIN BARGELD",
  menu_word: "Menü", menu_table_of: "Tisch", menu_pick: "wähle deine Karte", menu_things: "Artikel",
  menu_view_order: "Bestellung", menu_restart: "Neu anfangen", menu_pay: "Bezahlen",
  menu_add: "Hinzufügen", menu_update: "Aktualisieren", menu_remove: "Aus Bestellung entfernen",
  menu_edit_label: "BEARBEITEN", menu_add_label: "HINZU",
  restart_title: "Neu starten?", restart_body: "Deine Bestellung wird gelöscht und du beginnst von vorn.", restart_yes: "Ja, neu starten",
  receipt_your_account: "Deine Rechnung",
  receipt_total: "GESAMT", receipt_items_one: "Artikel", receipt_items_many: "Artikel",
  name_form_label: "FORM · 02", name_required: "ERFORDERLICH",
  name_caption: "Damit wir dich rufen können",
  name_title: "Wie heißt du?",
  name_handwritten: "wir rufen dich, wenn fertig",
  name_tag: "Hallo, ich heiße",
  upsell_one_more: "noch eine Kleinigkeit…", upsell_tap_open: "tippen zum Öffnen",
  upsell_from_chef: "VOM KOCH", upsell_no_thanks: "Nein, danke", upsell_yes_add: "Ja, nimm es",
  upsell_ps: "P.S.",
  review_caption: "Bestellung bestätigen", review_title: "Vor dem Bezahlen prüfen",
  review_handwritten: "kurz nachsehen", review_draft: "BELEG · ENTWURF",
  review_customer: "KUNDE", review_per_unit: "St", review_total: "GESAMT",
  review_tip_note: "* das Trinkgeld wird am Terminal gewählt",
  review_edit: "Bestellung ändern", review_confirm: "Bestätigen und bezahlen",
  guide_in_progress: "Zahlung läuft", guide_step_word: "Schritt", guide_order: "BEST",
  guide_help: "HILFE", guide_cancel: "ABBR.", guide_help_back: "Zurück",
  step_1: "Geh zum Terminal",
  step_2: "Trinkgeld auswählen",
  step_3: "Karte auflegen oder einstecken",
  step_4: "Grünen Knopf drücken",
  fail_caption: "fast…",
  fail_canceled: "Du hast abgebrochen", fail_expired: "Zeit abgelaufen",
  fail_rejected: "Deine Karte wurde abgelehnt", fail_network: "Verbindung fehlgeschlagen",
  fail_no_charge: "Es wurde nichts abgebucht.", fail_edit: "Bestellung ändern", fail_try_again: "Erneut versuchen",
  thanks_caption: "Danke", thanks_handwritten: "Prost,",
  thanks_show_title: "Bring den Beleg zur Bar",
  thanks_show_sub: "Deine Drinks kommen gleich.",
  thanks_order: "BEST",
};

const it: Bundle = {
  back: "Indietro", continue: "Continua", cancel: "Annulla", yes: "Sì", no: "No",
  need_help: "Hai bisogno di aiuto?",
  come_to_bar_title: "Vai al banco",
  come_to_bar_sub: "Ti aiutiamo subito.",
  card_only_main: "SOLO CARTA",
  card_only_sub: "NON ACCETTIAMO CONTANTI",
  menu_word: "Menu", menu_table_of: "tavola di", menu_pick: "scegli la tua carta", menu_things: "articoli",
  menu_view_order: "Vedi ordine", menu_restart: "Ricomincia", menu_pay: "Paga",
  menu_add: "Aggiungi", menu_update: "Aggiorna", menu_remove: "Rimuovi dall'ordine",
  menu_edit_label: "MODIFICA", menu_add_label: "AGGIUNGI",
  restart_title: "Ricominciare?", restart_body: "Il tuo ordine sarà cancellato e tornerai all'inizio.", restart_yes: "Sì, ricomincia",
  receipt_your_account: "Il tuo conto",
  receipt_total: "TOTALE", receipt_items_one: "articolo", receipt_items_many: "articoli",
  name_form_label: "FORM · 02", name_required: "RICHIESTO",
  name_caption: "Per chiamare il tuo ordine",
  name_title: "Come ti chiami?",
  name_handwritten: "ti chiameremo quando è pronto",
  name_tag: "Ciao, mi chiamo",
  upsell_one_more: "un'ultima cosa…", upsell_tap_open: "tocca per aprire",
  upsell_from_chef: "DALLO CHEF", upsell_no_thanks: "No, grazie", upsell_yes_add: "Sì, aggiungi",
  upsell_ps: "P.S.",
  review_caption: "Conferma il tuo ordine", review_title: "Controlla prima di pagare",
  review_handwritten: "controlla con calma", review_draft: "RICEVUTA · BOZZA",
  review_customer: "CLIENTE", review_per_unit: "cad", review_total: "TOTALE",
  review_tip_note: "* la mancia si sceglie al terminale",
  review_edit: "Modifica ordine", review_confirm: "Conferma e paga",
  guide_in_progress: "pagamento in corso", guide_step_word: "Passo", guide_order: "ORDINE",
  guide_help: "AIUTO", guide_cancel: "ANNULLA", guide_help_back: "Indietro",
  step_1: "Vai al terminale",
  step_2: "Scegli la mancia",
  step_3: "Avvicina o inserisci la carta",
  step_4: "Premi il pulsante verde",
  fail_caption: "quasi…",
  fail_canceled: "Hai annullato il pagamento", fail_expired: "Tempo scaduto",
  fail_rejected: "Carta rifiutata", fail_network: "Impossibile connettersi",
  fail_no_charge: "Nessun addebito è stato fatto.", fail_edit: "Modifica ordine", fail_try_again: "Riprova",
  thanks_caption: "Grazie", thanks_handwritten: "salute,",
  thanks_show_title: "Porta questa ricevuta al banco",
  thanks_show_sub: "Le tue bevande arrivano.",
  thanks_order: "ORD",
};

const zh: Bundle = {
  back: "返回", continue: "继续", cancel: "取消", yes: "是", no: "否",
  need_help: "需要帮助？",
  come_to_bar_title: "请到吧台",
  come_to_bar_sub: "我们马上来帮你。",
  card_only_main: "仅限刷卡",
  card_only_sub: "我们不接受现金",
  menu_word: "菜单", menu_table_of: "·", menu_pick: "选择你的牌", menu_things: "件",
  menu_view_order: "查看订单", menu_restart: "重新开始", menu_pay: "付款",
  menu_add: "加入", menu_update: "更新", menu_remove: "从订单移除",
  menu_edit_label: "编辑", menu_add_label: "添加",
  restart_title: "重新开始？", restart_body: "这将清除你的订单并回到首页。", restart_yes: "是的,重新开始",
  receipt_your_account: "你的账单",
  receipt_total: "合计", receipt_items_one: "件", receipt_items_many: "件",
  name_form_label: "表 · 02", name_required: "必填",
  name_caption: "为了叫到你",
  name_title: "你叫什么名字？",
  name_handwritten: "好了我们会叫你",
  name_tag: "你好,我叫",
  upsell_one_more: "最后一件事…", upsell_tap_open: "点击打开",
  upsell_from_chef: "主厨推荐", upsell_no_thanks: "不,谢谢", upsell_yes_add: "好,加上",
  upsell_ps: "附",
  review_caption: "确认你的订单", review_title: "付款前请核对",
  review_handwritten: "再看一眼", review_draft: "收据 · 草稿",
  review_customer: "客户", review_per_unit: "单价", review_total: "合计",
  review_tip_note: "* 小费在终端机上选择",
  review_edit: "编辑订单", review_confirm: "确认并付款",
  guide_in_progress: "付款中", guide_step_word: "步骤", guide_order: "订单",
  guide_help: "帮助", guide_cancel: "取消", guide_help_back: "返回",
  step_1: "请到刷卡终端",
  step_2: "在屏幕选择小费",
  step_3: "刷卡或插卡",
  step_4: "按下绿色按钮",
  fail_caption: "差一点…",
  fail_canceled: "你取消了付款", fail_expired: "时间到了",
  fail_rejected: "你的卡被拒绝", fail_network: "无法连接",
  fail_no_charge: "未扣款。", fail_edit: "修改订单", fail_try_again: "再试一次",
  thanks_caption: "谢谢", thanks_handwritten: "干杯,",
  thanks_show_title: "请把这张收据交给吧台",
  thanks_show_sub: "你的饮品马上就来。",
  thanks_order: "订单",
};

const ar: Bundle = {
  back: "رجوع", continue: "متابعة", cancel: "إلغاء", yes: "نعم", no: "لا",
  need_help: "تحتاج مساعدة؟",
  come_to_bar_title: "تعال إلى البار",
  come_to_bar_sub: "سنساعدك في الحال.",
  card_only_main: "بطاقة فقط",
  card_only_sub: "لا نقبل النقد",
  menu_word: "القائمة", menu_table_of: "·", menu_pick: "اختر بطاقتك", menu_things: "قطع",
  menu_view_order: "طلبي", menu_restart: "إعادة", menu_pay: "ادفع",
  menu_add: "أضف", menu_update: "تحديث", menu_remove: "حذف من الطلب",
  menu_edit_label: "تعديل", menu_add_label: "إضافة",
  restart_title: "إعادة؟", restart_body: "سيتم مسح طلبك والعودة إلى البداية.", restart_yes: "نعم، أعد",
  receipt_your_account: "حسابك",
  receipt_total: "الإجمالي", receipt_items_one: "قطعة", receipt_items_many: "قطع",
  name_form_label: "نموذج · 02", name_required: "مطلوب",
  name_caption: "لكي ننادي طلبك",
  name_title: "ما اسمك؟",
  name_handwritten: "سننادي عليك عند الجاهزية",
  name_tag: "مرحباً، اسمي",
  upsell_one_more: "شيء أخير…", upsell_tap_open: "انقر للفتح",
  upsell_from_chef: "من الشيف", upsell_no_thanks: "لا، شكراً", upsell_yes_add: "نعم، أضف",
  upsell_ps: "ملاحظة",
  review_caption: "تأكيد طلبك", review_title: "راجع قبل الدفع",
  review_handwritten: "تأكد بهدوء", review_draft: "إيصال · مسودة",
  review_customer: "العميل", review_per_unit: "للوحدة", review_total: "الإجمالي",
  review_tip_note: "* تختار البقشيش على الجهاز",
  review_edit: "تعديل الطلب", review_confirm: "تأكيد والدفع",
  guide_in_progress: "الدفع قيد التنفيذ", guide_step_word: "خطوة", guide_order: "طلب",
  guide_help: "مساعدة", guide_cancel: "إلغاء", guide_help_back: "رجوع",
  step_1: "اذهب إلى جهاز الدفع",
  step_2: "اختر البقشيش",
  step_3: "قرّب أو أدخل بطاقتك",
  step_4: "اضغط الزر الأخضر",
  fail_caption: "تقريباً…",
  fail_canceled: "ألغيت الدفع", fail_expired: "انتهى الوقت",
  fail_rejected: "تم رفض بطاقتك", fail_network: "تعذر الاتصال",
  fail_no_charge: "لم يتم خصم أي مبلغ.", fail_edit: "تعديل الطلب", fail_try_again: "حاول مرة أخرى",
  thanks_caption: "شكراً", thanks_handwritten: "بالصحة،",
  thanks_show_title: "خذ هذا الإيصال إلى البار",
  thanks_show_sub: "مشروباتك قادمة.",
  thanks_order: "طلب",
};

const ja: Bundle = {
  back: "戻る", continue: "続ける", cancel: "キャンセル", yes: "はい", no: "いいえ",
  need_help: "お手伝いが必要？",
  come_to_bar_title: "バーまでお越しください",
  come_to_bar_sub: "すぐにお手伝いします。",
  card_only_main: "カードのみ",
  card_only_sub: "現金はお受けできません",
  menu_word: "メニュー", menu_table_of: "·", menu_pick: "カードを選んで", menu_things: "点",
  menu_view_order: "注文を見る", menu_restart: "やり直す", menu_pay: "支払う",
  menu_add: "追加", menu_update: "更新", menu_remove: "注文から削除",
  menu_edit_label: "編集", menu_add_label: "追加",
  restart_title: "やり直しますか？", restart_body: "注文を消して最初に戻ります。", restart_yes: "はい、やり直す",
  receipt_your_account: "ご注文",
  receipt_total: "合計", receipt_items_one: "点", receipt_items_many: "点",
  name_form_label: "フォーム · 02", name_required: "必須",
  name_caption: "お呼びするために",
  name_title: "お名前は？",
  name_handwritten: "出来上がったらお呼びします",
  name_tag: "こんにちは、私の名前は",
  upsell_one_more: "最後にひとつ…", upsell_tap_open: "タップして開く",
  upsell_from_chef: "シェフから", upsell_no_thanks: "いいえ、結構です", upsell_yes_add: "はい、追加",
  upsell_ps: "P.S.",
  review_caption: "ご注文の確認", review_title: "支払い前に確認",
  review_handwritten: "ゆっくり確認してね", review_draft: "レシート · 下書き",
  review_customer: "お客様", review_per_unit: "単", review_total: "合計",
  review_tip_note: "* チップは端末で選択します",
  review_edit: "注文を編集", review_confirm: "確認して支払う",
  guide_in_progress: "支払い中", guide_step_word: "ステップ", guide_order: "注文",
  guide_help: "ヘルプ", guide_cancel: "キャンセル", guide_help_back: "戻る",
  step_1: "端末まで進んでください",
  step_2: "チップを選んでください",
  step_3: "カードをタップまたは挿入",
  step_4: "緑のボタンを押す",
  fail_caption: "もう少し…",
  fail_canceled: "支払いをキャンセルしました", fail_expired: "時間切れ",
  fail_rejected: "カードが拒否されました", fail_network: "接続できませんでした",
  fail_no_charge: "請求はされていません。", fail_edit: "注文を編集", fail_try_again: "もう一度",
  thanks_caption: "ありがとう", thanks_handwritten: "乾杯、",
  thanks_show_title: "このレシートをバーまで",
  thanks_show_sub: "お飲み物をお持ちします。",
  thanks_order: "注文",
};

const sv: Bundle = {
  back: "Tillbaka", continue: "Fortsätt", cancel: "Avbryt", yes: "Ja", no: "Nej",
  need_help: "Behöver du hjälp?",
  come_to_bar_title: "Kom till baren",
  come_to_bar_sub: "Vi hjälper dig direkt.",
  card_only_main: "ENDAST KORT",
  card_only_sub: "VI TAR INTE EMOT KONTANTER",
  menu_word: "Meny", menu_table_of: "bord", menu_pick: "välj ditt kort", menu_things: "varor",
  menu_view_order: "Se beställning", menu_restart: "Börja om", menu_pay: "Betala",
  menu_add: "Lägg till", menu_update: "Uppdatera", menu_remove: "Ta bort från beställning",
  menu_edit_label: "ÄNDRA", menu_add_label: "LÄGG TILL",
  restart_title: "Börja om?", restart_body: "Detta raderar din beställning och börjar om.", restart_yes: "Ja, börja om",
  receipt_your_account: "Din nota",
  receipt_total: "TOTALT", receipt_items_one: "vara", receipt_items_many: "varor",
  name_form_label: "FORM · 02", name_required: "OBLIGATORISKT",
  name_caption: "Så vi kan ropa upp dig",
  name_title: "Vad heter du?",
  name_handwritten: "vi ropar när det är klart",
  name_tag: "Hej, jag heter",
  upsell_one_more: "en sista grej…", upsell_tap_open: "tryck för att öppna",
  upsell_from_chef: "FRÅN KOCKEN", upsell_no_thanks: "Nej tack", upsell_yes_add: "Ja, lägg till",
  upsell_ps: "P.S.",
  review_caption: "Bekräfta beställning", review_title: "Kolla innan du betalar",
  review_handwritten: "kolla i lugn och ro", review_draft: "KVITTO · UTKAST",
  review_customer: "KUND", review_per_unit: "/st", review_total: "TOTALT",
  review_tip_note: "* dricks väljs på terminalen",
  review_edit: "Ändra beställning", review_confirm: "Bekräfta och betala",
  guide_in_progress: "betalning pågår", guide_step_word: "Steg", guide_order: "BEST",
  guide_help: "HJÄLP", guide_cancel: "AVBRYT", guide_help_back: "Tillbaka",
  step_1: "Gå till terminalen",
  step_2: "Välj dricks",
  step_3: "Blippa eller sätt i kortet",
  step_4: "Tryck på den gröna knappen",
  fail_caption: "nästan…",
  fail_canceled: "Du avbröt betalningen", fail_expired: "Tiden tog slut",
  fail_rejected: "Kortet nekades", fail_network: "Kunde inte ansluta",
  fail_no_charge: "Inget belopp drogs.", fail_edit: "Ändra beställning", fail_try_again: "Försök igen",
  thanks_caption: "Tack", thanks_handwritten: "skål,",
  thanks_show_title: "Ta kvittot till baren",
  thanks_show_sub: "Drinkarna kommer strax.",
  thanks_order: "BEST",
};

const af: Bundle = {
  back: "Terug", continue: "Gaan voort", cancel: "Kanselleer", yes: "Ja", no: "Nee",
  need_help: "Hulp nodig?",
  come_to_bar_title: "Kom na die kroeg",
  come_to_bar_sub: "Ons help jou nou.",
  card_only_main: "SLEGS KAART",
  card_only_sub: "ONS AANVAAR NIE KONTANT NIE",
  menu_word: "Spyskaart", menu_table_of: "tafel van", menu_pick: "kies jou kaart", menu_things: "items",
  menu_view_order: "Wys bestelling", menu_restart: "Begin oor", menu_pay: "Betaal",
  menu_add: "Voeg by", menu_update: "Werk by", menu_remove: "Verwyder uit bestelling",
  menu_edit_label: "WYSIG", menu_add_label: "VOEG BY",
  restart_title: "Begin oor?", restart_body: "Dit sal jou bestelling uitvee en weer begin.", restart_yes: "Ja, begin oor",
  receipt_your_account: "Jou rekening",
  receipt_total: "TOTAAL", receipt_items_one: "item", receipt_items_many: "items",
  name_form_label: "VORM · 02", name_required: "VERPLIG",
  name_caption: "Sodat ons jou kan roep",
  name_title: "Wat is jou naam?",
  name_handwritten: "ons roep jou wanneer dit reg is",
  name_tag: "Hallo, my naam is",
  upsell_one_more: "een laaste ding…", upsell_tap_open: "tik om oop te maak",
  upsell_from_chef: "VAN DIE SJEF", upsell_no_thanks: "Nee, dankie", upsell_yes_add: "Ja, voeg by",
  upsell_ps: "P.S.",
  review_caption: "Bevestig bestelling", review_title: "Kyk voor jy betaal",
  review_handwritten: "kyk rustig deur", review_draft: "BEWYS · KONSEP",
  review_customer: "KLIËNT", review_per_unit: "/st", review_total: "TOTAAL",
  review_tip_note: "* fooitjie word op die terminaal gekies",
  review_edit: "Wysig bestelling", review_confirm: "Bevestig en betaal",
  guide_in_progress: "betaling aan die gang", guide_step_word: "Stap", guide_order: "BEST",
  guide_help: "HULP", guide_cancel: "KANS.", guide_help_back: "Terug",
  step_1: "Gaan na die terminaal",
  step_2: "Kies jou fooitjie",
  step_3: "Tik of sit jou kaart in",
  step_4: "Druk die groen knoppie",
  fail_caption: "amper…",
  fail_canceled: "Jy het betaling gekanselleer", fail_expired: "Tyd is verby",
  fail_rejected: "Jou kaart is geweier", fail_network: "Kon nie verbind nie",
  fail_no_charge: "Geen bedrag is afgetrek nie.", fail_edit: "Wysig bestelling", fail_try_again: "Probeer weer",
  thanks_caption: "Dankie", thanks_handwritten: "gesondheid,",
  thanks_show_title: "Bring hierdie bewys na die kroeg",
  thanks_show_sub: "Jou drankies is op pad.",
  thanks_order: "BEST",
};

const BUNDLES: Record<LangCode, Bundle> = { en, es, pt, fr, de, it, zh, ar, ja, sv, af };

export function t(lang: LangCode | null, key: Key): string {
  const b = (lang && BUNDLES[lang]) || en;
  return b[key];
}
